import type { Transaction } from '../../types'
import { formatCurrency, formatDate, CONFIDENCE_THRESHOLD } from '../../lib/formatters'

interface RecentActivityProps {
  transactions: Transaction[]
  onViewAll?: () => void
}

export function RecentActivity({ transactions, onViewAll }: RecentActivityProps) {

  const typeLabel = (type: string) => {
    const labels: Record<string, string> = {
      capital_contribution: 'Capital Contribution',
      distribution: 'Distribution',
      member_distribution: 'Member Distribution',
      revenue: 'Revenue',
      expense: 'Expense',
      tax_payment: 'Tax Payment',
      intercompany_sale: 'Intercompany Sale'
    }
    return labels[type] || type
  }

  const flowLabel = (tx: Transaction) => {
    if (tx.sourceEntity && tx.destinationEntity) {
      return `${tx.sourceEntity} → ${tx.destinationEntity}`
    }
    return tx.counterparty || 'Unknown'
  }

  return (
    <div>
      {/* Section Header */}
      <div className="flex justify-between items-baseline mb-6 pb-4 border-b border-swiss-gray-200">
        <h2 className="text-swiss-label uppercase tracking-widest text-swiss-gray-500">
          Recent Activity
        </h2>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-swiss-caption text-swiss-gray-500 cursor-pointer transition-colors duration-150 hover:text-swiss-black"
          >
            View All →
          </button>
        )}
      </div>

      {/* Transactions Table */}
      <div className="border border-swiss-gray-200">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="text-left text-swiss-label uppercase tracking-widest text-swiss-gray-500 py-4 px-6 bg-swiss-off-white border-b border-swiss-gray-200">
                Date
              </th>
              <th className="text-left text-swiss-label uppercase tracking-widest text-swiss-gray-500 py-4 px-6 bg-swiss-off-white border-b border-swiss-gray-200">
                Type
              </th>
              <th className="text-left text-swiss-label uppercase tracking-widest text-swiss-gray-500 py-4 px-6 bg-swiss-off-white border-b border-swiss-gray-200">
                Flow
              </th>
              <th className="text-right text-swiss-label uppercase tracking-widest text-swiss-gray-500 py-4 px-6 bg-swiss-off-white border-b border-swiss-gray-200">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 px-6 text-center text-swiss-small text-swiss-gray-500">
                  No recent transactions
                </td>
              </tr>
            ) : (
              transactions.map((tx) => (
                <tr
                  key={tx.id}
                  className="transition-colors duration-150 hover:bg-swiss-off-white border-b border-swiss-gray-100 last:border-b-0"
                >
                  <td className="py-5 px-6 text-swiss-small">
                    <div className="font-swiss-mono text-swiss-caption text-swiss-gray-500">
                      {formatDate(tx.date)}
                    </div>
                  </td>
                  <td className="py-5 px-6 text-swiss-small">
                    <div className="font-medium">{typeLabel(tx.type)}</div>
                    {tx.confidence !== undefined && tx.confidence < CONFIDENCE_THRESHOLD && (
                      <div className="text-swiss-caption text-swiss-red mt-1">
                        Low confidence ({Math.round(tx.confidence * 100)}%)
                      </div>
                    )}
                  </td>
                  <td className="py-5 px-6 text-swiss-small text-swiss-gray-500">
                    {flowLabel(tx)}
                  </td>
                  <td className="py-5 px-6 text-swiss-small">
                    <div
                      className={`font-swiss-mono font-medium text-right ${
                        tx.amount > 0 ? 'text-swiss-green' : ''
                      }`}
                    >
                      {tx.amount > 0 && '+'}
                      {formatCurrency(tx.amount, 2)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
